import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  channelRefEquals,
  COLOR_CHANNEL_NAMES,
  colorChannelCount,
} from '../../core/channels';
import { findLayerById } from '../../core/layerTree';
import type { AlphaChannel, ChannelRef } from '../../core/types';
import { getEngine } from '../../engine/engine';
import {
  addChannel,
  channelToSelection,
  deleteChannel,
  duplicateChannel,
  renameChannel,
  selectionToChannel,
  setActiveChannel,
  setColorChannelVisible,
  showColorChannelAlone,
  toggleChannelVisible,
} from '../../state/maskActions';
import { getActiveDocument, getHistoryView, useAppStore } from '../../state/store';
import { Icon } from '../components/controls';
import { Panel } from './Panel';

const EYE_OPEN =
  'M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6zM12 9.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z';
const EYE_OFF = 'M4 4l16 16M2 12s4-6 10-6c2 0 3.7.7 5 1.6M22 12s-4 6-10 6c-2 0-3.7-.7-5-1.6';

/** Grey preview of a channel or of a layer mask, both single-channel bitmaps. */
function CoverageThumbnail({
  source,
  id,
  revision,
}: {
  source: 'channel' | 'mask';
  id: string;
  revision: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const doc = getActiveDocument(useAppStore.getState());
    if (!canvas || !doc) return;
    const engine = getEngine();
    if (!engine.available) return;

    const image =
      source === 'channel'
        ? engine.channelThumbnail(doc, id, 30)
        : engine.maskThumbnail(doc, id, 30);
    if (!image) return;
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d')?.putImageData(image, 0, 0);
  }, [source, id, revision]);

  return <canvas className="channel__thumb" ref={canvasRef} />;
}

interface RowProps {
  name: string;
  shortcut?: string;
  visible: boolean;
  active: boolean;
  onToggleVisible: () => void;
  onSelect: () => void;
  thumb?: ReactNode;
  onRename?: (name: string) => void;
}

function ChannelRow({
  name,
  shortcut,
  visible,
  active,
  onToggleVisible,
  onSelect,
  thumb,
  onRename,
}: RowProps) {
  const [renaming, setRenaming] = useState(false);

  return (
    <div
      className={`channel${active ? ' channel--active' : ''}`}
      onPointerDown={onSelect}
    >
      <button
        type="button"
        className={`layer__visibility${visible ? ' layer__visibility--on' : ''}`}
        title={visible ? `Hide ${name}` : `Show ${name}`}
        onClick={(event) => {
          event.stopPropagation();
          onToggleVisible();
        }}
      >
        <Icon size={14} path={visible ? EYE_OPEN : EYE_OFF} />
      </button>

      {thumb ?? <span className="channel__thumb channel__thumb--empty" />}

      {renaming && onRename ? (
        <input
          className="layer__name-input"
          autoFocus
          defaultValue={name}
          onBlur={(event) => {
            setRenaming(false);
            onRename(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
            if (event.key === 'Escape') setRenaming(false);
          }}
        />
      ) : (
        <span className="channel__name" onDoubleClick={() => onRename && setRenaming(true)}>
          {name}
        </span>
      )}
      {shortcut && <span className="channel__shortcut">{shortcut}</span>}
    </div>
  );
}

export function ChannelsPanel() {
  const store = useAppStore();
  const doc = getActiveDocument(store);
  const history = getHistoryView(store, store.activeDocumentId);
  const revision = history.position + history.labels.length * 1000 + store.pixelEpoch * 1e6;

  const active = store.activeChannel;
  const layer = doc ? findLayerById(doc.layers, doc.activeLayerId) : null;
  const colorsShown = doc ? colorChannelCount(doc.colorVisible) : 0;
  const alphaChannel = (ref: ChannelRef): AlphaChannel | null =>
    ref.kind === 'alpha' ? (doc?.channels.find((item) => item.id === ref.id) ?? null) : null;
  const selectedAlpha = alphaChannel(active);

  const visibleFlag = (index: 0 | 1 | 2): boolean => {
    if (!doc) return false;
    return index === 0 ? doc.colorVisible.r : index === 1 ? doc.colorVisible.g : doc.colorVisible.b;
  };

  return (
    <Panel
      id="channels"
      title="Channels"
      actions={
        <>
          <button
            type="button"
            className="button button--icon"
            title="Channel to selection"
            disabled={!selectedAlpha}
            onClick={() => selectedAlpha && channelToSelection(selectedAlpha.id)}
          >
            <Icon size={14} path="M4 4h7v7H4zM13 13h7v7h-7zM11 11l2 2" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Selection to channel"
            disabled={!doc?.hasSelection}
            onClick={selectionToChannel}
          >
            <Icon size={14} path="M4 4h16v16H4zM8 8h8v8H8z" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="New channel"
            disabled={!doc}
            onClick={() => addChannel(false)}
          >
            <Icon size={14} path="M12 5v14M5 12h14" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Duplicate channel"
            disabled={!selectedAlpha}
            onClick={() => selectedAlpha && duplicateChannel(selectedAlpha.id)}
          >
            <Icon size={14} path="M9 9h11v11H9zM4 4h11v3M4 4v11h3" />
          </button>
          <button
            type="button"
            className="button button--icon button--danger"
            title="Delete channel"
            disabled={!selectedAlpha}
            onClick={() => selectedAlpha && deleteChannel(selectedAlpha.id)}
          >
            <Icon size={14} path="M5 7h14M9 7V4h6v3M7 7l1 13h8l1-13" />
          </button>
        </>
      }
    >
      <div className="channel-list">
        {doc && (
          <>
            <ChannelRow
              name="RGB"
              shortcut="Ctrl+2"
              visible={colorsShown === 3}
              active={active.kind === 'composite'}
              onToggleVisible={() => showColorChannelAlone(null)}
              onSelect={() => setActiveChannel({ kind: 'composite' })}
            />
            {COLOR_CHANNEL_NAMES.map((name, index) => {
              const channelIndex = index as 0 | 1 | 2;
              return (
                <ChannelRow
                  key={name}
                  name={name}
                  shortcut={`Ctrl+${3 + index}`}
                  visible={visibleFlag(channelIndex)}
                  active={active.kind === 'color' && active.index === channelIndex}
                  onToggleVisible={() =>
                    setColorChannelVisible(channelIndex, !visibleFlag(channelIndex))
                  }
                  onSelect={() => setActiveChannel({ kind: 'color', index: channelIndex })}
                />
              );
            })}

            {layer?.hasMask && (
              <ChannelRow
                name={`${layer.name} Mask`}
                visible={store.maskView !== 'off'}
                active={channelRefEquals(active, { kind: 'mask', layerId: layer.id })}
                onToggleVisible={() =>
                  store.setMaskView(store.maskView === 'off' ? 'overlay' : 'off')
                }
                onSelect={() => setActiveChannel({ kind: 'mask', layerId: layer.id })}
                thumb={<CoverageThumbnail source="mask" id={layer.id} revision={revision} />}
              />
            )}

            {doc.channels.map((channel) => (
              <ChannelRow
                key={channel.id}
                name={channel.name}
                visible={channel.visible}
                active={channelRefEquals(active, { kind: 'alpha', id: channel.id })}
                onToggleVisible={() => toggleChannelVisible(channel.id)}
                onSelect={() => setActiveChannel({ kind: 'alpha', id: channel.id })}
                onRename={
                  channel.kind === 'alpha' ? (name) => renameChannel(channel.id, name) : undefined
                }
                thumb={<CoverageThumbnail source="channel" id={channel.id} revision={revision} />}
              />
            ))}
          </>
        )}
      </div>
    </Panel>
  );
}
